import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ArrowRight, CheckCircle, Award, Youtube, Instagram, Mic,
  Repeat, DollarSign, BarChart3, Users, Zap, PenTool
} from 'lucide-react';

const channels = [
  { icon: Youtube, name: "YouTube", desc: "Review AREA in videos, tutorials, and market updates." },
  { icon: Instagram, name: "Instagram / TikTok", desc: "Share your referral link in bios, stories, and reels." },
  { icon: Mic, name: "Podcasts", desc: "Mention AREA in episodes and show notes." },
  { icon: PenTool, name: "Newsletters", desc: "Feature AREA in your email list with tracked links." },
  { icon: Users, name: "Communities", desc: "Share in Facebook groups, Discord servers, and forums." },
  { icon: Zap, name: "Courses", desc: "Recommend AREA to students in your programs." },
];

const earnings = [
  { scenario: "10 Pro referrals/month", monthly: "$247.50", annual: "$2,970", note: "25% of $99 x 10" },
  { scenario: "5 Founder referrals", monthly: "—", annual: "$1,498.50", note: "30% of $999 x 5 (one-time)" },
  { scenario: "Mix: 20 Pro + 3 Founder", monthly: "$495/mo recurring", annual: "$6,837.30", note: "Blended rates" },
];

export default function AffiliateInfluencers() {
  return (
    <div className="min-h-screen bg-white" data-testid="affiliate-influencers">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <img src="/images/area-logo.png" alt="AREA" className="h-10" />
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-gray-600">
            <Link to="/affiliate" className="hover:text-[#1e3a5f] transition-colors">Affiliate</Link>
            <Link to="/affiliate/influencers" className="text-[#1e3a5f] font-semibold">For Creators</Link>
            <Link to="/affiliate/brokerages" className="hover:text-[#1e3a5f] transition-colors">For Brokerages</Link>
            <Link to="/login" className="hover:text-[#1e3a5f] transition-colors">Sign In</Link>
          </div>
          <Link to="/register"><Button className="bg-[#1e3a5f] hover:bg-[#152d4a] text-white px-6">Join AREA</Button></Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#f5f0ff] to-white"></div>
        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-50 text-violet-700 text-sm font-semibold mb-8">
            <Award className="w-4 h-4" /> Creator Partnerships
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0f1f33] leading-tight tracking-tight mb-6" data-testid="influencer-hero-title">
            Built for creators who influence real estate
          </h1>
          <p className="text-lg sm:text-xl text-gray-500 max-w-3xl mx-auto leading-relaxed mb-10">
            Turn your audience, content, and community into recurring partner revenue by promoting AREA membership.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/affiliate/apply">
              <Button size="lg" className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white px-8 py-6 text-lg font-semibold shadow-lg">
                Apply as a Partner <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/affiliate">
              <Button size="lg" variant="outline" className="border-gray-300 text-gray-700 px-8 py-6 text-lg">Explore the Program</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Earnings Examples */}
      <section className="py-20 bg-[#fafbfd]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-violet-600 font-semibold text-sm tracking-wider uppercase mb-3">Earnings Potential</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0f1f33] mb-4">Real numbers, real recurring income</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">Commissions are lifetime after attribution locks. Here's what realistic creator scenarios look like.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {earnings.map((e, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm text-center">
                <p className="text-sm font-medium text-gray-500 mb-4">{e.scenario}</p>
                {e.monthly !== "—" && <p className="text-3xl font-bold text-[#0f1f33] mb-1">{e.monthly}</p>}
                {e.monthly !== "—" && <p className="text-sm text-gray-400 mb-3">per month recurring</p>}
                <p className="text-2xl font-bold text-emerald-600">{e.annual}</p>
                <p className="text-xs text-gray-400 mt-1">{e.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Content Channels */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-violet-600 font-semibold text-sm tracking-wider uppercase mb-3">Channels</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0f1f33]">Promote AREA anywhere you create</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {channels.map((c, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center mb-3">
                  <c.icon className="w-5 h-5 text-violet-600" />
                </div>
                <h3 className="font-semibold text-[#0f1f33] mb-1">{c.name}</h3>
                <p className="text-sm text-gray-500">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Creators Choose AREA */}
      <section className="py-20 bg-[#fafbfd]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0f1f33]">Why creators choose AREA</h2>
          </div>
          <div className="space-y-4">
            {[
              { icon: Repeat, text: "Lifetime recurring commissions — not one-time bonuses" },
              { icon: DollarSign, text: "Up to 30% commission on Founder memberships" },
              { icon: BarChart3, text: "Real-time dashboard to track clicks, signups, and earnings" },
              { icon: CheckCircle, text: "Custom campaign links with source tracking" },
              { icon: Users, text: "Promote a real product that RE professionals actually use" },
              { icon: Zap, text: "Direct deposit payouts — fast, direct, and transparent" },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-4 bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                <div className="w-10 h-10 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-violet-600" />
                </div>
                <p className="text-[#0f1f33] font-medium">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#0f1f33]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">Start earning as an AREA creator partner</h2>
          <p className="text-lg text-gray-300 mb-10">Apply today and start turning your influence into recurring revenue.</p>
          <Link to="/affiliate/apply">
            <Button size="lg" className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white px-10 py-6 text-lg font-semibold shadow-lg">
              Apply Now <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="bg-white border-t border-gray-100 py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-400">
          <img src="/images/area-logo.png" alt="AREA" className="h-6" />
          <div className="flex gap-6">
            <Link to="/affiliate/terms" className="hover:text-gray-600">Affiliate Terms</Link>
            <Link to="/privacy" className="hover:text-gray-600">Privacy</Link>
          </div>
          <span>&copy; {new Date().getFullYear()} American Real Estate Alliance</span>
        </div>
      </footer>
    </div>
  );
}
